import React from 'react';
import { Award, Target, Heart, TrendingUp, Star, Shield, Users, Zap, Quote } from 'lucide-react';
import { companyInfo } from '../data/mock';

const About = () => {
  const values = [
    {
      icon: Award,
      title: 'Excellence',
      description: 'We set the highest standards in everything we do.'
    },
    {
      icon: Target,
      title: 'Precision',
      description: 'Meticulous attention to detail in every placement.'
    },
    {
      icon: Heart,
      title: 'Passion',
      description: 'Genuine love for hospitality drives our success.'
    },
    {
      icon: TrendingUp,
      title: 'Growth',
      description: 'Committed to continuous improvement and innovation.'
    }
  ];

  return (
    <section id="about" className="py-28 bg-gradient-to-b from-white via-slate-50/30 to-white relative overflow-hidden">
      {/* Premium Background Elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-shsTeal/20 to-transparent" />
      <div className="absolute top-40 -right-40 w-[600px] h-[600px] bg-shsTeal/5 rounded-full blur-[100px]" />
      <div className="absolute bottom-40 -left-40 w-[500px] h-[500px] bg-shsGold/5 rounded-full blur-[100px]" />
      
      {/* Decorative Lines */}
      <div className="absolute top-32 left-10 w-32 h-px bg-gradient-to-r from-shsTeal/30 to-transparent" />
      <div className="absolute bottom-32 right-10 w-32 h-px bg-gradient-to-l from-shsGold/30 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-shsTeal/10 to-shsBlue/10 rounded-full text-sm font-semibold mb-6 border border-shsTeal/20">
            <Users className="w-4 h-4 text-shsTeal" />
            <span className="text-shsTeal">About Us</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-shsNavy mb-6 leading-tight">
            Your Partner in
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-shsTeal via-emerald-500 to-shsBlue">
              Hospitality Excellence
            </span>
          </h2>
          <p className="text-gray-600 text-lg lg:text-xl leading-relaxed">
            Dedicated to transforming guest experiences through exceptional service and expertise
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-start">
          {/* Founder Section - Super Premium */}
          <div className="relative">
            {/* Glow Effect */}
            <div className="absolute -inset-4 bg-gradient-to-br from-shsTeal/20 via-shsBlue/10 to-shsGold/20 rounded-[40px] blur-2xl opacity-50" />
            
            <div className="relative bg-white rounded-[32px] p-8 lg:p-10 shadow-2xl shadow-shsTeal/10 border border-gray-100">
              {/* Founder Photo - Large & Prominent */}
              <div className="flex flex-col items-center mb-8">
                <div className="relative mb-6">
                  {/* Animated Ring */}
                  <div className="absolute -inset-3 bg-gradient-to-r from-shsTeal via-shsBlue to-shsGold rounded-full opacity-30 animate-spin" style={{ animationDuration: '10s' }} />
                  <div className="absolute -inset-2 bg-gradient-to-r from-shsGold via-shsTeal to-shsBlue rounded-full opacity-20" />
                  
                  {/* Photo Container */}
                  <div className="relative w-52 h-52 lg:w-60 lg:h-60 rounded-full overflow-hidden ring-4 ring-white shadow-2xl">
                    <img
                      src={companyInfo.founder.image}
                      alt={companyInfo.founder.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  
                  {/* Badge */}
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-gradient-to-r from-shsGold to-amber-500 text-shsNavy px-6 py-2.5 rounded-full text-sm font-bold shadow-lg shadow-shsGold/30 whitespace-nowrap">
                    Founder & Director
                  </div>
                </div>

                {/* Name & Title */}
                <h3 className="text-3xl lg:text-4xl font-bold text-shsNavy text-center mt-6">
                  {companyInfo.founder.name}
                </h3>
                <p className="text-shsTeal font-semibold text-lg">
                  {companyInfo.founder.title}
                </p>
              </div>

              {/* Bio with Quote Style */}
              <div className="relative px-4">
                <Quote className="absolute -top-2 -left-2 w-10 h-10 text-shsTeal/10" />
                <p className="text-gray-600 leading-relaxed text-center lg:text-lg italic">
                  "{companyInfo.founder.bio}"
                </p>
                <Quote className="absolute -bottom-2 -right-2 w-10 h-10 text-shsTeal/10 rotate-180" />
              </div>

              {/* Achievement Stats */}
              <div className="grid grid-cols-4 gap-4 mt-10 pt-8 border-t border-gray-100">
                {[
                  { icon: Star, value: '5-Star', label: 'Rating' },
                  { icon: Shield, value: '100%', label: 'Quality' },
                  { icon: Users, value: '24/7', label: 'Support' },
                  { icon: Zap, value: 'Fast', label: 'Response' }
                ].map((item, index) => (
                  <div key={index} className="text-center group cursor-pointer">
                    <div className="w-12 h-12 mx-auto bg-gradient-to-br from-shsTeal/10 to-shsBlue/10 rounded-xl flex items-center justify-center mb-2 group-hover:from-shsTeal group-hover:to-shsBlue group-hover:scale-110 transition-all duration-300">
                      <item.icon className="w-5 h-5 text-shsTeal group-hover:text-white transition-colors" />
                    </div>
                    <p className="text-sm font-bold text-shsNavy">{item.value}</p>
                    <p className="text-xs text-gray-500">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Values Grid - Enhanced */}
          <div className="space-y-8">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-shsNavy/5 rounded-full text-xs font-bold text-shsNavy mb-4">
                <Award className="w-3 h-3" />
                What Drives Us
              </div>
              <h3 className="text-3xl lg:text-4xl font-bold text-shsNavy">Our Core Values</h3>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-5">
              {values.map((value, index) => (
                <div
                  key={index}
                  className="group relative p-6 bg-white rounded-2xl hover:shadow-2xl hover:shadow-shsTeal/15 transition-all duration-500 border border-gray-100 hover:border-shsTeal/30 overflow-hidden"
                >
                  {/* Hover Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-shsTeal/5 to-shsBlue/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="relative z-10">
                    <div className="w-14 h-14 bg-gradient-to-br from-shsTeal/10 to-shsBlue/10 rounded-2xl flex items-center justify-center mb-4 group-hover:from-shsTeal group-hover:to-shsBlue group-hover:scale-110 transition-all duration-300 shadow-lg shadow-transparent group-hover:shadow-shsTeal/20">
                      <value.icon className="w-7 h-7 text-shsTeal group-hover:text-white transition-colors" />
                    </div>
                    <h4 className="text-xl font-bold text-shsNavy mb-2 group-hover:text-shsTeal transition-colors">
                      {value.title}
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Premium CTA Card */}
            <div className="mt-10 p-8 bg-gradient-to-r from-shsNavy via-shsBlue to-shsNavy rounded-3xl text-white relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
              
              <div className="relative z-10 flex flex-col sm:flex-row items-center justify-between gap-6">
                <div>
                  <p className="font-bold text-xl mb-1">Ready to elevate your hospitality?</p>
                  <p className="text-white/70">Let's discuss how we can help your property.</p>
                </div>
                <button 
                  onClick={() => document.querySelector('#contact').scrollIntoView({ behavior: 'smooth' })}
                  className="px-8 py-4 bg-shsGold text-shsNavy font-bold rounded-full hover:bg-white transition-all duration-300 hover:scale-105 whitespace-nowrap shadow-lg hover:shadow-xl"
                >
                  Contact Us
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
